import { TemplateType } from '@/types/boot';
import { TemplateConfigurations, getTemplateConfig, type TemplateFeatures } from '@/constants/templateConfig';
import { isFirstTimeDepositor, isFtdLocalApmMethod, isSupportedLastPaymentMethod } from '@/constants/lastPaymentMethod';
import { PreferredPaymentView } from '@/templateViews/PreferredPaymentView';
import { expressMethodForDevice } from '@/utils/deviceExpressSupport';
import { isPaymentMethodAvailable, pickFirstNonExpressPaymentMethod } from '@/utils/paymentMethodResolvers';
import { isSavedPaypalAccount } from '@/utils/paypal';
import { logWarn } from '@/utils/logger';
import { PaymentMethodEnum } from '@/types/common';
import type { PaymentMethod } from '@/types/checkout';
import type { Template } from '@/types/template';
import {
  CheckoutTemplate,
  type PaymentMethodSelectionContext,
  type TemplateInitContext,
  type TemplateView,
} from './CheckoutTemplate';

/**
 * Mobile-only "preferred payment method" template.
 *
 * One BE wire enum (`PREFERRED_PAYMENT_METHOD`), two FE behaviors keyed
 * on boot flags the BE already populates:
 *
 *   - FTD branch — `isFirstTimeDepositor === true`. Surfaces a local
 *     APM the BE nominated for the shopper's market (BLIK / PIX, see
 *     {@link SUPPORTED_FTD_LOCAL_APM_METHODS}) as the only primary CTA,
 *     falling back to the device's native express wallet (Apple Pay on
 *     iOS, Google Pay on Android). Eligible only when the nominated APM
 *     or that wallet is actually present in the BE-returned
 *     `paymentMethods`.
 *
 *   - LAST-METHOD branch — returning depositor with a
 *     `preferredPaymentMethod` (see {@link lastUsedPaymentMethod} for the
 *     two sources) set to a method we have a renderer for AND that method
 *     present in the BE-returned `paymentMethods`. Surfaces that method as the only
 *     primary CTA. PRD-scoped to
 *     {apple, google, paypal, link, card, amazon_pay, blik, blik_checkout, pix};
 *     see {@link SUPPORTED_LAST_PAYMENT_METHODS}. CARD renders the
 *     new-card form as the primary CTA whether or not the user has a
 *     saved card.
 *
 * Both branches render through {@link PreferredPaymentView}, which
 * exposes a "Use other payment method" secondary link that swaps to
 * DEFAULT (escape hatch).
 *
 * Every soft-fail edge case (no last method, unrenderable method,
 * method missing from BE paymentMethods, desktop, no payment methods)
 * returns `isEligible === false` so the registry silently falls back to
 * DEFAULT. Hard configuration errors are reserved for templates whose
 * misconfiguration should surface to the user (see SinglePaymentTemplate);
 * for this template the spec explicitly requires graceful degradation.
 */
export class PreferredPaymentTemplate extends CheckoutTemplate {
  readonly type: TemplateType = TemplateType.PREFERRED_PAYMENT_METHOD;
  readonly View: TemplateView = PreferredPaymentView;
  readonly features: TemplateFeatures = TemplateConfigurations[TemplateType.PREFERRED_PAYMENT_METHOD];

  isEligible(ctx: TemplateInitContext): boolean {
    // Mobile-first template, but also runs on a phone in landscape — the
    // single-CTA + "Use Other Payment Method" UI is just rendered inside
    // the existing 40/60 split shell. Desktop (>=1024 or tall viewport)
    // still falls back to DEFAULT: `isLandscape()` caps at 1023×500.
    if (!ctx.isMobile && !ctx.isLandscape) return false;
    if (!ctx.paymentMethods || ctx.paymentMethods.length === 0) return false;
    return this.isFtdEligible(ctx) || this.isLastMethodEligible(ctx);
  }

  /**
   * Default primary method for the active branch.
   *
   * Selection mirrors `isEligible`:
   *   - FTD → the BE-nominated local APM (BLIK / PIX) when the market
   *     has one, else the device's native express wallet (falls back to
   *     first non-express only as a defensive last-resort; in practice
   *     FTD would have been ineligible without either present).
   *   - LAST-METHOD → the BE-supplied `preferredPaymentMethod`,
   *     re-validated against `paymentMethods` so a stale boot can't
   *     pre-select something the user can't actually pay with.
   *   - otherwise → undefined (caller decides).
   */
  resolveDefaultPaymentMethod(ctx: PaymentMethodSelectionContext): PaymentMethodEnum | undefined {
    if (isFirstTimeDepositor(ctx.bootData?.checkoutConfiguration)) {
      const localApm = this.ftdNominatedLocalApm(ctx, ctx.paymentMethods);
      if (localApm) return localApm;
      const expressForDevice = expressMethodForDevice(ctx.device);
      if (expressForDevice) return expressForDevice;
      return pickFirstNonExpressPaymentMethod(ctx.paymentMethods);
    }

    const last = this.lastUsedPaymentMethod(ctx);
    if (!last) return undefined;
    if (!isPaymentMethodAvailable(last, ctx.paymentMethods)) return undefined;

    // TEMP (remove once `blik_checkout` is deprecated and the 2 affected
    // publishers no longer expose both variants): on publishers that
    // expose BOTH `blik` and `blik_checkout` in `paymentMethods`, BE
    // collapses the returning-depositor `preferredPaymentMethod` to
    // `blik` even when the user actually paid with `blik_checkout`. Pick
    // whichever variant appears first in `paymentMethods` so those
    // publishers can steer selection via their configured order; normal
    // publishers that list only one variant fall through unchanged.
    if (last === PaymentMethodEnum.BLIK) {
      return this.resolveBlikVariant(ctx.paymentMethods);
    }

    return last;
  }

  /**
   * If the device's express wallet (Apple Pay on iOS / Google Pay on
   * Android) fails to mount at runtime, this template has nothing
   * useful to show — its whole point is a single primary CTA. Swap to
   * DEFAULT so the user gets the full method picker.
   *
   * Fires for both branches when the active method is apple/google;
   * no-op for paypal/link (those SDKs don't call the wallet-readiness
   * callback wired through {@link useExpressWalletFallback}).
   */
  override onExpressWalletUnavailable(): Template {
    return getTemplateConfig(TemplateType.DEFAULT);
  }

  private isFtdEligible(ctx: TemplateInitContext): boolean {
    if (!isFirstTimeDepositor(ctx.bootData?.checkoutConfiguration)) return false;
    if (this.ftdNominatedLocalApm(ctx, ctx.paymentMethods!)) return true;
    return isPaymentMethodAvailable(expressMethodForDevice(ctx.device), ctx.paymentMethods!);
  }

  /**
   * The local APM (BLIK / PIX) the BE nominated as this first-time
   * depositor's primary CTA, or `undefined` when the market has none —
   * in which case the FTD branch keeps its device-express-wallet
   * behaviour untouched.
   *
   * Read primarily from `/checkout/v3`
   * (`checkoutConfigurations.checkoutTemplate.preferredPaymentMethod`),
   * which is resolved against the live session; the boot JWT's
   * `preferredPaymentMethod` is accepted as a fallback so the FE works
   * whichever of the two the BE populates for an FTD. Both are narrowed
   * by {@link isFtdLocalApmMethod}, so a nomination outside BLIK / PIX
   * (`google` in today's payloads, `card`, an unknown string, …) is
   * ignored here and the device wallet still wins — the returning-
   * depositor `preferredPaymentMethod` contract is untouched.
   *
   * A nominated method missing from the BE-returned `paymentMethods`
   * (BLIK/PIX not enabled for the publisher) degrades exactly like the
   * last-method branch: `undefined` here, so FTD either falls back to
   * the device wallet or, absent that, to the DEFAULT template.
   */
  private ftdNominatedLocalApm(
    { bootData, checkoutData }: Pick<TemplateInitContext, 'bootData' | 'checkoutData'>,
    paymentMethods: PaymentMethod[]
  ): PaymentMethodEnum | undefined {
    const nominated =
      checkoutData?.checkoutConfigurations?.checkoutTemplate?.preferredPaymentMethod ??
      bootData?.checkoutConfiguration?.preferredPaymentMethod;
    if (!isFtdLocalApmMethod(nominated)) return undefined;
    if (!isPaymentMethodAvailable(nominated, paymentMethods)) return undefined;
    // Same BLIK / BLIK_CHECKOUT ambiguity the last-method branch handles:
    // publishers exposing both variants steer the pick via their configured
    // `paymentMethods` order. See {@link resolveBlikVariant}.
    if (nominated === PaymentMethodEnum.BLIK) return this.resolveBlikVariant(paymentMethods);
    return nominated;
  }

  /**
   * Returning depositor + BE flagged a renderable last method that the
   * BE-returned `paymentMethods` actually still serves. Both soft-fail
   * branches log a Coralogix warning so BE drift (unknown method names,
   * filter mismatches) surfaces before users notice.
   *
   * BE contract: `preferredPaymentMethod` is the canonical
   * `PaymentMethodEnum` string ('card' | 'google' | 'apple' | …) — any
   * value outside `SUPPORTED_LAST_PAYMENT_METHODS` is real BE drift and
   * worth a `logWarn`.
   */
  private isLastMethodEligible({ bootData, paymentMethods, checkoutData }: TemplateInitContext): boolean {
    if (isFirstTimeDepositor(bootData?.checkoutConfiguration)) return false;
    const last = this.lastUsedPaymentMethod({ bootData, checkoutData });
    if (!last) return false;

    if (!isSupportedLastPaymentMethod(last)) {
      logWarn('preferredPaymentMethod not renderable; falling back to DEFAULT', { method: last });
      return false;
    }

    if (!isPaymentMethodAvailable(last, paymentMethods!)) {
      logWarn('preferredPaymentMethod absent from BE paymentMethods; falling back to DEFAULT', { method: last });
      return false;
    }

    // CARD is only worth the single-CTA last-method UI when there's a saved
    // card to one-tap. With NO saved card the "last method" form is just an
    // empty new-card form — identical to what DEFAULT shows but without the
    // method picker — so fall back to DEFAULT (the full picker) instead.
    if (last === PaymentMethodEnum.CREDIT_CARD && !this.hasSavedCard(checkoutData)) {
      return false;
    }

    return true;
  }

  /**
   * The method a returning depositor last paid with, as the canonical
   * `PaymentMethodEnum` string ('card' | 'google' | 'apple' | …).
   *
   * Two payloads carry it and the BE populates one or the other depending on
   * the session: `/checkout/v3`
   * (`checkoutConfigurations.checkoutTemplate.preferredPaymentMethod`), resolved
   * against the live session, and the boot JWT's `preferredPaymentMethod`.
   * Reading only the boot meant a session whose boot carried nothing but
   * `{ template: 'PREFERRED_PAYMENT_METHOD', isFirstTimeDepositor: false }`
   * failed eligibility and fell back to DEFAULT — losing the one-tap CTA and
   * the bottom sheet — even though `/checkout/v3` did name the method.
   *
   * The `/checkout/v3` echo wins when present, mirroring
   * {@link ftdNominatedLocalApm}, but ONLY when that echo agrees this is a
   * returning depositor. An echo asserting `isFirstTimeDepositor` carries an FTD
   * *nomination* — a market APM the shopper has never actually used — which
   * belongs to the FTD branch and must not be mistaken for a last-used method.
   */
  private lastUsedPaymentMethod({
    bootData,
    checkoutData,
  }: Pick<TemplateInitContext, 'bootData' | 'checkoutData'>): PaymentMethodEnum | undefined {
    const echo = checkoutData?.checkoutConfigurations?.checkoutTemplate;
    const fromCheckout = echo?.isFirstTimeDepositor === true ? undefined : echo?.preferredPaymentMethod;
    return (fromCheckout ?? bootData?.checkoutConfiguration?.preferredPaymentMethod) as PaymentMethodEnum | undefined;
  }

  /**
   * Order-based tie-break for the BLIK / BLIK_CHECKOUT ambiguity — see
   * the TEMP comment in {@link resolveDefaultPaymentMethod}. Caller has
   * already confirmed at least `BLIK` is present in `paymentMethods`, so
   * the fallback to `PaymentMethodEnum.BLIK` is only defense-in-depth.
   */
  private resolveBlikVariant(paymentMethods: TemplateInitContext['paymentMethods']): PaymentMethodEnum {
    const firstBlikVariant = paymentMethods!.find(
      (pm) => pm.paymentMethodName === PaymentMethodEnum.BLIK || pm.paymentMethodName === PaymentMethodEnum.BLIK_CHECKOUT
    );
    const chosen = (firstBlikVariant?.paymentMethodName as PaymentMethodEnum | undefined) ?? PaymentMethodEnum.BLIK;
    if (chosen !== PaymentMethodEnum.BLIK) {
      logWarn('preferredPaymentMethod=blik remapped to blik_checkout by paymentMethods-order tie-break', {
        chosen,
      });
    }
    return chosen;
  }

  private hasSavedCard(checkoutData: TemplateInitContext['checkoutData']): boolean {
    return (
      (checkoutData?.savedCards?.length ?? 0) > 0 &&
      (checkoutData?.savedCards?.some((card) => !isSavedPaypalAccount(card)) ?? false)
    );
  }
}
